import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Checkbox, FormControlLabel, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Divider,
  Grid, Card, CardContent, TablePagination, Alert, CircularProgress,
  TableSortLabel, Button,
} from '@mui/material';
import { Assessment, Download } from '@mui/icons-material';
import axios from 'axios';
import { exportToExcel, fmtDateIST, fmtNum } from '../utils/exportExcel';

const PAGE_OPTS = [10, 20, 50, 100];

function fmt(v, decimals = 2) {
  if (v == null) return '-';
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function toIST(d) {
  if (!d) return '-';
  let s = String(d);
  if (!s.endsWith('Z') && !s.includes('+')) s += 'Z';
  return new Date(s).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

function PnlCell({ value, pct, open }) {
  if (open) return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Open</Typography>
    </Box>
  );
  if (value == null) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const color = value > 0 ? '#4CAF50' : value < 0 ? '#E8392E' : 'text.secondary';
  return (
    <Box>
      <Typography variant="body2" fontWeight={700} sx={{ color, lineHeight: 1.3 }}>
        {value >= 0 ? '+' : ''}{fmt(value)}
      </Typography>
      {pct != null && (
        <Typography variant="caption" sx={{ color, opacity: 0.85 }}>
          {value >= 0 ? '+' : ''}{pct.toFixed(2)}%
        </Typography>
      )}
    </Box>
  );
}

function CumPnlCell({ value, open }) {
  if (open || value == null) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const color = value > 0 ? '#4CAF50' : value < 0 ? '#E8392E' : 'text.secondary';
  return (
    <Typography variant="body2" fontWeight={700} sx={{ color }}>
      {value >= 0 ? '+' : ''}{fmt(value)}
    </Typography>
  );
}

export default function ReportsPage({ apiUrl, tradeMode = 'paper' }) {
  const [strategies, setStrategies] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [trades, setTrades] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStrategies = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/reports/strategies`, { headers, params: { trade_mode: tradeMode } });
      setStrategies(res.data || []);
      const withTrades = (res.data || []).filter(s => s.trade_count > 0).map(s => s.id);
      setSelectedIds(withTrades);
    } catch (err) { console.error(err); }
  }, [apiUrl, tradeMode]);

  const fetchTrades = useCallback(async () => {
    if (selectedIds.length === 0) { setTrades([]); setTotal(0); setSummary(null); return; }
    setLoading(true);
    try {
      const params = {
        trade_mode: tradeMode,
        strategy_ids: selectedIds.join(','),
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      const res = await axios.get(`${apiUrl}/reports/trades`, { headers, params });
      setTrades(res.data.trades || []);
      setTotal(res.data.total || 0);
      setSummary(res.data.summary || null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiUrl, tradeMode, selectedIds, page, rowsPerPage, sortBy, sortOrder]);

  useEffect(() => { fetchStrategies(); }, [fetchStrategies]);
  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const handleExport = async () => {
    if (selectedIds.length === 0) return;
    setExporting(true);
    try {
      // Fetch all trades (unlimited) for export
      const [tradesRes, stratRes] = await Promise.all([
        axios.get(`${apiUrl}/reports/trades`, {
          headers,
          params: { trade_mode: tradeMode, strategy_ids: selectedIds.join(','), limit: 50000, sort_order: 'asc' },
        }),
        axios.get(`${apiUrl}/reports/strategies`, { headers, params: { trade_mode: tradeMode } }),
      ]);
      const allTrades = tradesRes.data.trades || [];
      const sum = tradesRes.data.summary || {};
      const strats = stratRes.data || [];

      const modeLabel = tradeMode === 'paper' ? 'Paper' : 'Real';
      const sheets = [
        {
          name: 'Summary',
          headers: ['Metric', 'Value'],
          rows: [
            ['Export Date (IST)', fmtDateIST(new Date().toISOString())],
            ['Trade Mode', modeLabel],
            ['Strategies Exported', strats.filter(s => selectedIds.includes(s.id)).map(s => s.name).join(', ')],
            ['Total Trades', sum.total_trades ?? ''],
            ['Win Rate (%)', sum.win_rate ?? ''],
            ['Net Profit', fmtNum(sum.net_profit)],
            ['Gross Profit', fmtNum(sum.gross_profit)],
            ['Gross Loss', fmtNum(sum.gross_loss)],
            ['Profit Factor', sum.profit_factor ?? ''],
            ['Winning Trades', sum.winning_trades ?? ''],
            ['Losing Trades', sum.losing_trades ?? ''],
          ],
          colWidths: [28, 32],
        },
        {
          name: 'Strategies',
          headers: ['Strategy Name', 'Trade Count', 'Net P&L'],
          rows: strats.filter(s => selectedIds.includes(s.id)).map(s => [
            s.name, s.trade_count, fmtNum(s.total_pnl),
          ]),
        },
        {
          name: 'Trades',
          headers: ['Trade #', 'Direction', 'Symbol', 'Entry Time (IST)', 'Exit Time (IST)', 'Entry Price', 'Exit Price', 'Qty', 'Net P&L', 'Net P&L %', 'Cumulative P&L', 'Status'],
          rows: allTrades.map(t => [
            t.trade_num, t.direction,
            t.entry?.symbol || t.exit?.symbol || '',
            t.entry ? fmtDateIST(t.entry.created_at) : '',
            t.exit ? fmtDateIST(t.exit.created_at) : 'Open',
            t.entry ? fmtNum(t.entry.entry_price) : '',
            t.exit ? fmtNum(t.exit.entry_price) : '',
            t.position_size,
            t.exit ? fmtNum(t.net_pnl) : '',
            t.exit ? fmtNum(t.net_pnl_pct) : '',
            t.exit ? fmtNum(t.cumulative_pnl) : '',
            t.exit ? (t.net_pnl > 0 ? 'Win' : t.net_pnl < 0 ? 'Loss' : 'Breakeven') : 'Open',
          ]),
        },
      ];

      exportToExcel(`${modeLabel}_Trade_Reports_${new Date().toISOString().slice(0, 10)}`, sheets);
    } catch (err) { console.error(err); }
    finally { setExporting(false); }
  };

  const toggleStrategy = (id) => { setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); setPage(0); };
  const selectAll = () => { setSelectedIds(strategies.map(s => s.id)); setPage(0); };
  const selectNone = () => { setSelectedIds([]); setPage(0); };

  const handleSort = (col) => {
    setSortOrder(sortBy === col && sortOrder === 'asc' ? 'desc' : 'asc');
    setSortBy(col);
    setPage(0);
  };

  const modeLabel = tradeMode === 'paper' ? 'Paper' : 'Real';
  const modeColor = tradeMode === 'paper' ? '#3A5BA0' : '#E8392E';

  return (
    <Box data-testid={`reports-${tradeMode}-page`}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Assessment sx={{ color: modeColor }} />
        <Typography variant="h5">{modeLabel} Trade Reports</Typography>
        <Chip label={modeLabel.toUpperCase()} size="small" sx={{ bgcolor: modeColor, color: '#fff', fontWeight: 700 }} />
      </Box>

      <Grid container spacing={2}>
        {/* Left: Strategy Selector */}
        <Grid item xs={12} md={2.5}>
          <Paper sx={{ p: 1.5, maxHeight: 'calc(100vh - 180px)', overflow: 'auto' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Strategies</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box display="flex" gap={0.5} mb={1}>
              <Chip label="All" size="small" onClick={selectAll} clickable sx={{ fontSize: '0.65rem', fontWeight: 600 }} />
              <Chip label="None" size="small" onClick={selectNone} clickable variant="outlined" sx={{ fontSize: '0.65rem', fontWeight: 600 }} />
            </Box>
            {strategies.length === 0 ? (
              <Typography variant="caption" color="text.secondary">No strategies found</Typography>
            ) : (
              strategies.map(s => (
                <Box key={s.id} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  py: 0.3, px: 0.5, borderRadius: 1, mb: 0.3,
                  bgcolor: selectedIds.includes(s.id) ? 'rgba(58,91,160,0.06)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(58,91,160,0.04)' },
                }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={selectedIds.includes(s.id)} onChange={() => toggleStrategy(s.id)} sx={{ p: 0.3 }} />}
                    label={
                      <Box>
                        <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.2, display: 'block' }}>{s.name}</Typography>
                        <Typography variant="caption" sx={{ color: s.total_pnl >= 0 ? '#4CAF50' : '#E8392E', fontSize: '0.65rem' }}>
                          {s.trade_count} trades | {s.total_pnl >= 0 ? '+' : ''}{fmt(s.total_pnl)}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        {/* Right: Results */}
        <Grid item xs={12} md={9.5}>
          {/* Summary Cards */}
          {summary && (
            <Grid container spacing={1} sx={{ mb: 1.5 }}>
              {[
                { label: 'Total Trades', value: summary.total_trades, color: '#3A5BA0' },
                { label: 'Win Rate', value: `${summary.win_rate}%`, color: summary.win_rate >= 50 ? '#4CAF50' : '#E8392E' },
                { label: 'Net Profit', value: `${summary.net_profit >= 0 ? '+' : ''}${fmt(summary.net_profit)}`, color: summary.net_profit >= 0 ? '#4CAF50' : '#E8392E' },
                { label: 'Profit Factor', value: summary.profit_factor === 999.99 ? '∞' : summary.profit_factor, color: '#FF9800' },
                { label: 'Winners', value: summary.winning_trades, color: '#4CAF50' },
                { label: 'Losers', value: summary.losing_trades, color: '#E8392E' },
              ].map((c, i) => (
                <Grid item xs={4} sm={2} key={i}>
                  <Card sx={{ borderTop: `3px solid ${c.color}` }}>
                    <CardContent sx={{ p: '8px !important' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{c.label}</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: c.color }}>{c.value}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Trade Table */}
          <Paper>
            <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(58,91,160,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight={700}>Trade List ({total})</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {loading && <CircularProgress size={16} />}
                <Button
                  size="small" variant="outlined" startIcon={<Download />}
                  onClick={handleExport}
                  disabled={exporting || selectedIds.length === 0}
                  data-testid={`export-${tradeMode}-reports-btn`}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {exporting ? 'Exporting...' : 'Export Excel'}
                </Button>
              </Box>
            </Box>
            <Divider />

            {selectedIds.length === 0 ? (
              <Alert severity="info" sx={{ m: 1 }}>Select one or more strategies from the left panel to view trades.</Alert>
            ) : trades.length === 0 && !loading ? (
              <Alert severity="info" sx={{ m: 1 }}>No trades found for the selected strategies.</Alert>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 80 }}>
                          <TableSortLabel active={sortBy === 'trade_num'} direction={sortOrder} onClick={() => handleSort('trade_num')}>
                            Trade #
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ minWidth: 55 }}>Dir</TableCell>
                        <TableCell sx={{ minWidth: 80 }}>Symbol</TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          <TableSortLabel active={sortBy === 'created_at'} direction={sortOrder} onClick={() => handleSort('created_at')}>
                            Entry Time
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>Exit Time</TableCell>
                        <TableCell align="right" sx={{ minWidth: 90 }}>Entry Price</TableCell>
                        <TableCell align="right" sx={{ minWidth: 90 }}>Exit Price</TableCell>
                        <TableCell align="right" sx={{ minWidth: 50 }}>Qty</TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>Net P&amp;L</TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>Cumulative P&amp;L</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trades.map((t, idx) => {
                        const entry = t.entry;
                        const exit = t.exit;
                        const isOpen = !exit;
                        const dirColor = t.direction === 'Long' ? '#4CAF50' : t.direction === 'Short' ? '#E8392E' : '#FF9800';
                        const rowBg = !isOpen
                          ? (t.net_pnl > 0 ? 'rgba(76,175,80,0.03)' : t.net_pnl < 0 ? 'rgba(232,57,46,0.03)' : 'transparent')
                          : 'rgba(255,152,0,0.02)';

                        return (
                          <TableRow
                            key={idx}
                            data-testid={`trade-row-${t.trade_num}`}
                            sx={{
                              bgcolor: rowBg,
                              '&:hover': { bgcolor: 'rgba(58,91,160,0.04)' },
                              borderLeft: `3px solid ${isOpen ? '#FF9800' : (t.net_pnl >= 0 ? '#4CAF50' : '#E8392E')}`,
                            }}
                          >
                            {/* Trade # */}
                            <TableCell>
                              <Typography variant="body2" fontWeight={700} color="primary">
                                {t.trade_num}
                              </Typography>
                            </TableCell>

                            {/* Direction */}
                            <TableCell>
                              <Chip
                                label={t.direction}
                                size="small"
                                sx={{
                                  fontSize: '0.6rem', fontWeight: 700, height: 18,
                                  bgcolor: `${dirColor}18`, color: dirColor, border: `1px solid ${dirColor}`,
                                }}
                              />
                            </TableCell>

                            {/* Symbol */}
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {entry?.symbol || exit?.symbol || '-'}
                              </Typography>
                            </TableCell>

                            {/* Entry Time */}
                            <TableCell>
                              <Typography variant="caption" sx={{ lineHeight: 1.3, display: 'block' }}>
                                {entry ? toIST(entry.created_at) : '-'}
                              </Typography>
                            </TableCell>

                            {/* Exit Time */}
                            <TableCell>
                              {isOpen ? (
                                <Chip label="Open" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(255,152,0,0.1)', color: '#FF9800', border: '1px solid #FF9800', fontWeight: 700 }} />
                              ) : (
                                <Typography variant="caption" sx={{ lineHeight: 1.3, display: 'block' }}>
                                  {toIST(exit.created_at)}
                                </Typography>
                              )}
                            </TableCell>

                            {/* Entry Price */}
                            <TableCell align="right">
                              <Typography variant="body2">{entry ? fmt(entry.entry_price) : '-'}</Typography>
                            </TableCell>

                            {/* Exit Price */}
                            <TableCell align="right">
                              <Typography variant="body2">{exit ? fmt(exit.entry_price) : '-'}</Typography>
                            </TableCell>

                            {/* Qty */}
                            <TableCell align="right">
                              <Typography variant="body2">{t.position_size}</Typography>
                            </TableCell>

                            {/* Net P&L */}
                            <TableCell align="right">
                              <PnlCell value={isOpen ? null : t.net_pnl} pct={isOpen ? null : t.net_pnl_pct} open={isOpen} />
                            </TableCell>

                            {/* Cumulative P&L */}
                            <TableCell align="right">
                              <CumPnlCell value={isOpen ? null : t.cumulative_pnl} open={isOpen} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={PAGE_OPTS}
                  component="div"
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, p) => setPage(p)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
